const chapter = {
  id: 'structural-organisation-animals',
  slug: 'structural-organisation-animals',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Structural Organisation in Animals',
        hindi: 'प्राणियों में संरचनात्मक संगठन',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Structural Organisation', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'animal-tissues',
      title: {
        english: 'Animal Tissues',
        hindi: 'जन्तु ऊतक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Four basic types: **Epithelial, Connective, Muscular, Neural**.',
        hindi: 'चार बुनियादी प्रकार: **उपकला, संयोजी, मांसपेशीय, तंत्रिका**। **उपकला** - शरीर की सतहों, रेखा गुहाओं को कवर करता है; कोशिकाएँ सघन रूप से पैक, छोटी अंतरकोशिकीय आव्यूह; तहखाने की झिल्ली मौजूद.',
            },
            {
              english: '**Epithelial** — covers body surfaces, lines cavities; cells compactly packed, little intercellular matrix; basement membrane present.',
        hindi: '**उपकला** - शरीर की सतहों, रेखा गुहाओं को कवर करता है; कोशिकाएँ सघन रूप से पैक, छोटी अंतरकोशिकीय आव्यूह; तहखाने की झिल्ली मौजूद.',
            },
            {
              english: '• **Simple** (single layer):',
        hindi: '• **सरल** (एकल परत):',
            },
            {
              english: '  – **Squamous** — flat, scale-like; walls of blood vessels, alveoli (forms diffusion boundaries).',
        hindi: '- **स्क्वैमस** - सपाट, स्केल-जैसा; रक्त वाहिकाओं की दीवारें, एल्वियोली (प्रसार सीमाएँ बनाती हैं)।',
            },
            {
              english: '  – **Cuboidal** — ducts of glands, tubular parts of nephron; secretion + absorption.',
        hindi: '- **क्यूबॉइडल** - ग्रंथियों की नलिकाएं, नेफ्रॉन के ट्यूबलर भाग; स्राव + अवशोषण.',
            },
            {
              english: '  – **Columnar** — tall slender; lining of stomach, intestine; secretion + absorption.',
        hindi: '- **स्तंभकार** - लंबा पतला; पेट, आंत की परत; स्राव + अवशोषण.',
            },
            {
              english: '  – **Ciliated columnar / cuboidal** — bronchioles, fallopian tubes; movement of particles/mucus.',
        hindi: '- **सिलिअटेड कॉलमर / क्यूबॉइडल** - ब्रोन्किओल्स, फैलोपियन ट्यूब; particles/mucus का संचलन।',
            },
            {
              english: '• **Compound** — many layers; protection (skin, buccal cavity, pharynx, ducts of salivary glands, pancreatic ducts).',
        hindi: '• **यौगिक** — कई परतें; सुरक्षा (त्वचा, मुख गुहा, ग्रसनी, लार ग्रंथियों की नलिकाएं, अग्न्याशय नलिकाएं)। **ग्लैंडुलर एपिथेलियम**: **एककोशिकीय** - आंत की गॉब्लेट कोशिकाएं; **बहुकोशिकीय** - लार ग्रंथियाँ। स्राव का तरीका: **एक्सोक्राइन** (वाहिका के माध्यम से - बलगम, लार, कान का मैल, तेल, दूध, पाचन एंजाइम); **एंडोक्राइन** (कोई वाहिनी नहीं → रक्त - हार्मोन)।',
            },
            {
              english: '**Glandular epithelium**: **Unicellular** — goblet cells of intestine; **Multicellular** — salivary glands. Mode of secretion: **Exocrine** (via duct — mucus, saliva, earwax, oil, milk, digestive enzymes); **Endocrine** (no duct → blood — hormones).',
        hindi: '**ग्लैंडुलर एपिथेलियम**: **एककोशिकीय** - आंत की गॉब्लेट कोशिकाएं; **बहुकोशिकीय** - लार ग्रंथियाँ। स्राव का तरीका: **एक्सोक्राइन** (वाहिका के माध्यम से - बलगम, लार, कान का मैल, तेल, दूध, पाचन एंजाइम); **एंडोक्राइन** (कोई वाहिनी नहीं → रक्त - हार्मोन)।',
            },
            {
              english: 'Cell junctions: **Tight** (prevent leakage), **Adhering** (cement), **Gap** (rapid communication).',
        hindi: 'सेल जंक्शन: **तंग** (रिसाव को रोकें), **पालन** (सीमेंट), **गैप** (तेजी से संचार)।',
            },
          ],
        },
      ],
    },
    {
      id: 'connective',
      title: {
        english: 'Connective Tissue',
        hindi: 'संयोजी ऊतक',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Most abundant + widely distributed in body of complex animals; cells loose in matrix (jelly / fluid / dense / rigid).',
        hindi: 'जटिल जानवरों के शरीर में सबसे प्रचुर + व्यापक रूप से वितरित; कोशिकाएं आव्यूह में ढीली हो जाती हैं (जेली/द्रव/सघन/कठोर)।',
            },
            {
              english: 'Three categories:',
        hindi: 'तीन श्रेणियां:',
            },
            {
              english: '• **Loose connective** — Areolar (between skin & muscles, around blood vessels, nerves; macrophages, mast cells, fibroblasts); Adipose (fat storage; below skin — thermal insulation).',
        hindi: '• **ढीला संयोजक** - एरिओलर (त्वचा और मांसपेशियों के बीच, रक्त वाहिकाओं, तंत्रिकाओं के आसपास; मैक्रोफेज, मस्तूल कोशिकाएं, फ़ाइब्रोब्लास्ट); वसा (वसा भंडारण; त्वचा के नीचे - थर्मल इन्सुलेशन)।',
            },
            {
              english: '• **Dense connective** — Dense regular (collagen fibres parallel — **tendon** muscle→bone; **ligament** bone→bone); Dense irregular (skin, fibrous fibres oriented in many directions).',
        hindi: '• **घना संयोजी** - सघन नियमित (कोलेजन फाइबर समानांतर - **कण्डरा** मांसपेशी→हड्डी; **लिगामेंट** हड्डी→हड्डी); घने अनियमित (त्वचा, कई दिशाओं में उन्मुख रेशेदार फाइबर)।',
            },
            {
              english: '• **Specialised connective**:',
        hindi: '• **विशेष संयोजक**:',
            },
            {
              english: '  – **Cartilage** — solid, flexible, chondrocytes in lacunae; tip of nose, outer ear, intervertebral discs; **chondroitin** in matrix.',
        hindi: '- **उपास्थि** - ठोस, लचीला, लैकुने में चोंड्रोसाइट्स; नाक की नोक, बाहरी कान, इंटरवर्टेब्रल डिस्क; **चोंड्रोइटिन** आव्यूह में।',
            },
            {
              english: '  – **Bone** — hard, calcified matrix (CaPO₄, **calcium salts + collagen**); osteocytes in **lacunae** connected by **canaliculi**; **Haversian canals** with blood vessels.',
        hindi: '- **हड्डी** - कठोर, कैल्सीफाइड आव्यूह (CaPO₄, **कैल्शियम लवण + कोलेजन**); **लैकुने** में ऑस्टियोसाइट्स **कैनालिकुली** से जुड़े होते हैं; **हैवेरियन नहरें** रक्त वाहिकाओं के साथ।',
            },
            {
              english: '  – **Blood** — fluid CT; matrix = **plasma**; cells = RBC + WBC + platelets.',
        hindi: '- **रक्त** - द्रव सीटी; मैट्रिक्स = **प्लाज्मा**; कोशिकाएँ = RBC + WBC + प्लेटलेट्स।',
            },
          ],
        },
      ],
    },
    {
      id: 'muscle-neural',
      title: {
        english: 'Muscular & Neural Tissues',
        hindi: 'पेशीय और तन्त्रिका ऊतक',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Feature', 'Skeletal', 'Smooth (Visceral)', 'Cardiac'],
          rows: [
            ['Location', 'Attached to bones', 'Stomach, intestine, blood vessels, uterus', 'Heart only'],
            ['Striations', 'Striated', 'Non-striated', 'Striated'],
            ['Nuclei', 'Multinucleate', 'Uninucleate', 'Uninucleate (rarely 2)'],
            ['Control', 'Voluntary', 'Involuntary', 'Involuntary'],
            ['Branching', 'Unbranched', 'Unbranched', 'Branched; **intercalated discs** between fibres'],
            ['Fatigue', 'Fatigues quickly', 'Slow, sustained', 'Never fatigues'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Neural tissue** — exerts greatest control over body responsiveness.',
        hindi: '**तंत्रिका ऊतक** - शरीर की प्रतिक्रियाशीलता पर सबसे बड़ा नियंत्रण रखता है।',
            },
            {
              english: 'Neurons (functional unit): cell body + dendrites + axon. **Neuroglia** support + insulate (> 50 % of nervous tissue volume).',
        hindi: 'न्यूरॉन्स (कार्यात्मक इकाई): कोशिका शरीर + डेंड्राइट + अक्षतंतु। **न्यूरोग्लिया** सपोर्ट + इंसुलेट (> तंत्रिका ऊतक की मात्रा का 50%)।',
            },
            {
              english: 'Stimulus → membrane potential disturbed → wave travels → effector responds.',
        hindi: 'उत्तेजना → झिल्ली क्षमता परेशान → तरंग यात्रा → प्रभावकार प्रतिक्रिया करता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'earthworm',
      title: {
        english: 'Earthworm — *Pheretima posthuma*',
        hindi: 'केंचुआ - *फेरेटिमा पोस्टहुमा*',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Reddish-brown terrestrial invertebrate; "**farmer\'s friend**" — burrowing → aeration + drainage of soil.',
        hindi: 'लाल-भूरा स्थलीय अकशेरुकी; "**किसान मित्र**" - खोदना → मिट्टी का वातन + जल निकासी।',
            },
            {
              english: 'Body cylindrical, 100-120 segments (metameric); **clitellum** in segments **14-16-17** (forms cocoon).',
        hindi: 'शरीर बेलनाकार, 100-120 खंड (मेटामेरिक); **क्लाइटेलम** खंडों में **14-16-17** (कोकून बनाता है)।',
            },
            {
              english: 'Anterior end has mouth + prostomium (sensory).',
        hindi: 'अग्र सिरे पर मुख + प्रोस्टोमियम (संवेदी) होता है।',
            },
            {
              english: 'Setae present on each segment (except 1st, last & clitellum) → locomotion.',
        hindi: 'सेटे प्रत्येक खंड पर मौजूद हैं (पहले, आखिरी और क्लाइटेलम को छोड़कर) → गति।',
            },
            {
              english: 'Genital openings: **Female pore on 14th segment**; **male pores on 18th segment**; **4 pairs spermathecal apertures (segments 6-9)** receive sperms during mating.',
        hindi: 'जननांग छिद्र: **14वें खंड पर महिला छिद्र**; **18वें खंड पर पुरुष छिद्र**; **4 जोड़े स्पर्माथेकल एपर्चर (सेगमेंट 6-9)** संभोग के दौरान शुक्राणु प्राप्त करते हैं। पाचन तंत्र: मुंह → मुख गुहा (1-3) → ग्रसनी → अन्नप्रणाली (5-7) → मांसपेशी ** गिजार्ड** (8-9, पीस) → पेट (9-14) → आंत (15-अंत) - इसमें **टाइफ्लोसोल** (अनुदैर्ध्य तह) होता है ↑ सतह क्षेत्र।',
            },
            {
              english: 'Digestive tract: mouth → buccal cavity (1-3) → pharynx → oesophagus (5-7) → muscular **gizzard** (8-9, grinds) → stomach (9-14) → intestine (15-end) — has **typhlosole** (longitudinal fold) ↑ surface area.',
        hindi: 'पाचन तंत्र: मुंह → मुख गुहा (1-3) → ग्रसनी → अन्नप्रणाली (5-7) → मांसपेशी **गिज़र्ड** (8-9, पीस) → पेट (9-14) → आंत (15-अंत) - **टाइफ्लोसोल** (अनुदैर्ध्य तह) ↑ सतह क्षेत्र है।',
            },
            {
              english: 'Respiration — through moist skin (cutaneous).',
        hindi: 'श्वसन - नम त्वचा (त्वचीय) के माध्यम से।',
            },
            {
              english: 'Closed circulation; haemoglobin dissolved in plasma; pseudohearts.',
        hindi: 'बंद परिसंचरण; प्लाज्मा में घुला हुआ हीमोग्लोबिन; छद्महृदय.',
            },
            {
              english: 'Excretion — **nephridia** (3 types: septal, integumentary, pharyngeal).',
        hindi: 'उत्सर्जन - **नेफ्रिडिया** (3 प्रकार: सेप्टल, पूर्णांक, ग्रसनी)। **हर्मैफ्रोडाइट** लेकिन क्रॉस-निषेचन (2 व्यक्ति शुक्राणुओं का आदान-प्रदान करते हैं); क्लाइटेलम द्वारा बिछाया गया कोकून → भ्रूण सीधे विकसित होते हैं (कोई लार्वा नहीं)।',
            },
            {
              english: '**Hermaphrodite** but cross-fertilisation (2 individuals exchange sperms); cocoon laid by clitellum → embryos develop directly (no larva).',
        hindi: '**उभयलिंगी** लेकिन क्रॉस-निषेचन (2 व्यक्ति शुक्राणुओं का आदान-प्रदान करते हैं); क्लाइटेलम द्वारा बिछाया गया कोकून → भ्रूण सीधे विकसित होते हैं (कोई लार्वा नहीं)।',
            },
          ],
        },
      ],
    },
    {
      id: 'cockroach',
      title: {
        english: 'Cockroach — *Periplaneta americana*',
        hindi: 'कॉकरोच - *पेरिप्लानेटा अमेरिकाना*',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Insect; nocturnal, omnivorous; 34-53 mm; bright yellow/brown/black.',
        hindi: 'कीट; रात्रिचर, सर्वाहारी; 34-53 मिमी; चमकीला yellow/brown/काला।',
            },
            {
              english: 'Body: **head + thorax + abdomen**, covered by chitinous exoskeleton; sclerites — dorsal **tergites**, ventral **sternites** joined by arthrodial membrane.',
        hindi: 'शरीर: **सिर + वक्ष + पेट**, चिटिनस एक्सोस्केलेटन से ढका हुआ; स्क्लेराइट्स - पृष्ठीय **टेरगाइट्स**, उदर **स्टर्नाइट्स** आर्थ्रोडियल झिल्ली से जुड़े हुए।',
            },
            {
              english: 'Head — triangular, formed by fusion of 6 segments; movable joint with thorax (neck); **compound eyes**; **antennae** with sensory receptors.',
        hindi: 'सिर - त्रिकोणीय, 6 खंडों के संलयन से बना; वक्ष (गर्दन) के साथ चल जोड़; **संयुक्त नेत्र**; **एंटीना** संवेदी रिसेप्टर्स के साथ।',
            },
            {
              english: 'Mouth parts (biting & chewing): labrum (upper lip), mandibles, maxillae (with palps), labium (lower lip), hypopharynx (tongue).',
        hindi: 'मुंह के भाग (काटना और चबाना): लैब्रम (ऊपरी होंठ), मेम्बिबल्स, मैक्सिला (पल्प्स के साथ), लेबियम (निचला होंठ), हाइपोफरीनक्स (जीभ)।',
            },
            {
              english: 'Thorax: 3 segments — pro-, meso-, meta-thorax; 1st pair wings (mesothorax) = **mesothoracic wings = tegmina** (leathery); 2nd pair (metathorax) = membranous; 3 pairs of legs.',
        hindi: 'थोरैक्स: 3 खंड - प्रो-, मेसो-, मेटा-थोरैक्स; पहली जोड़ी पंख (मेसोथोरैक्स) = **मेसोथोरेसिक wings = tegmina** (leathery); दूसरी जोड़ी (मेटाथोरैक्स) = झिल्लीदार; 3 जोड़ी पैर.',
            },
            {
              english: 'Abdomen: **10 segments** in both sexes. **Female has 7 visible sterna; male has 9**. Male bears **anal styles** (absent in female).',
        hindi: 'पेट: दोनों लिंगों में **10 खंड**। **महिला के पास 7 दृश्यमान स्टर्ना होते हैं; पुरुष के पास 9** है। नर भालू **गुदा शैलियाँ** (मादा में अनुपस्थित)।',
            },
            {
              english: 'Digestive: mouth → pharynx → oesophagus → **crop** (storage) → **gizzard/proventriculus** (chitinous teeth — grinding) → midgut (gastric caeca — 6-8 secrete digestive juice) → hindgut → anus.',
        hindi: 'पाचन: मुंह → ग्रसनी → अन्नप्रणाली → **फसल** (भंडारण) → **gizzard/proventriculus** (चिटिनस दांत - पीसना) → मध्य आंत (गैस्ट्रिक अका - 6-8 पाचन रस स्रावित करता है) → पश्च आंत → गुदा।',
            },
            {
              english: '**Malpighian tubules** at midgut-hindgut junction → **excretion** (uricotelic).',
        hindi: '**मैल्पीघियन नलिकाएं** मिडगुट-हिंदगुट जंक्शन पर → **उत्सर्जन** (यूरिकोटेलिक)।',
            },
            {
              english: 'Respiration — **tracheal system** with **10 pairs of spiracles**.',
        hindi: 'श्वसन - **श्वासनली प्रणाली** **10 जोड़ी श्वासरंध्र** के साथ।',
            },
            {
              english: 'Circulation — open; 13-chambered tubular heart on dorsal side; haemolymph colourless (no respiratory pigment).',
        hindi: 'सर्कुलेशन - खुला; पृष्ठीय भाग पर 13-कक्षीय ट्यूबलर हृदय; हेमोलिम्फ रंगहीन (कोई श्वसन वर्णक नहीं)।',
            },
            {
              english: 'Sexes separate; male reproductive — testes, vas deferens, mushroom gland; **ootheca** by female — 16 eggs; **paurometabolous** development (nymph → adult through moulting; no pupa).',
        hindi: 'लिंग अलग; नर प्रजनन - वृषण, वास डिफेरेंस, मशरूम ग्रंथि; **यूथेका** मादा द्वारा - 16 अंडे; **पौरोमेटाबोलस** विकास (निम्फ → निर्मोचन के माध्यम से वयस्क; कोई प्यूपा नहीं)।',
            },
          ],
        },
      ],
    },
    {
      id: 'frog',
      title: {
        english: 'Frog — *Rana tigrina*',
        hindi: 'मेंढक - *राणा टिग्रीना*',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Amphibian; ectothermic; **summer sleep = aestivation**; **winter sleep = hibernation**.',
        hindi: 'उभयचर; एक्टोथर्मिक; **ग्रीष्म sleep = aestivation**; **सर्दी sleep = hibernation**।',
            },
            {
              english: 'Skin moist; respiration via **lungs (on land), buccal cavity & skin (cutaneous — in water)**.',
        hindi: 'त्वचा नम; **फेफड़ों (भूमि पर), मुख गुहा और त्वचा (त्वचीय - पानी में)** के माध्यम से श्वसन।',
            },
            {
              english: 'Sexual dimorphism: male has **vocal sacs + nuptial pads** on first digit of forelimb; female lacks these.',
        hindi: 'यौन द्विरूपता: पुरुष के अग्रपाद के पहले अंक पर **मुखर थैली + गुप्त पैड** होते हैं; महिलाओं में इनका अभाव है।',
            },
            {
              english: 'Digestive — short alimentary canal (carnivore); bilobed liver; pancreas; cloaca.',
        hindi: 'पाचन - छोटी आहार नाल (मांसाहारी); द्विपालीय यकृत; अग्न्याशय; क्लोअका',
            },
            {
              english: 'Heart — **3-chambered (2 atria + 1 ventricle)**; mixed blood; renal portal + hepatic portal systems.',
        hindi: 'हृदय - **3-कक्षीय (2 अटरिया + 1 निलय)**; मिश्रित रक्त; वृक्क पोर्टल + यकृत पोर्टल प्रणाली।',
            },
            {
              english: 'Excretion — **ureotelic**; mesonephric kidneys.',
        hindi: 'उत्सर्जन - **यूरियोटेलिक**; मेसोनेफ्रिक गुर्दे।',
            },
            {
              english: 'Brain — fore-, mid-, hindbrain; 10 pairs of cranial nerves.',
        hindi: 'मस्तिष्क - अग्र-, मध्य-, पश्च मस्तिष्क; कपाल तंत्रिकाओं के 10 जोड़े।',
            },
            {
              english: 'Reproduction — sexes separate; **external fertilisation in water**; female lays 2500-3000 eggs at a time; **indirect development** through tadpole larva (metamorphosis).',
        hindi: 'प्रजनन - लिंग अलग; **पानी में बाह्य निषेचन**; मादा एक बार में 2500-3000 अंडे देती है; **अप्रत्यक्ष विकास** टैडपोल लार्वा (कायापलट) के माध्यम से।',
            },
            {
              english: 'Economic importance — eaten as food; controls insect pests; used in medical research.',
        hindi: 'आर्थिक महत्व - भोजन के रूप में खाया जाता है; कीड़ों को नियंत्रित करता है; चिकित्सा अनुसंधान में उपयोग किया जाता है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Tissues = Epithelial + Connective + Muscular + Neural}',
      desc: {
        english: '',
        hindi: 'ऊतक = उपकला + संयोजी + पेशीय + तंत्रिका',
      },
    },
    {
      latex: '\\text{Tendon = muscle} \\to \\text{bone; Ligament = bone} \\to \\text{bone}',
      desc: {
        english: '',
        hindi: 'टेंडन = मांसपेशी की हड्डी; लिगामेंट = हड्डी की हड्डी',
      },
    },
    {
      latex: '\\text{Cardiac muscle: striated, branched, intercalated discs, involuntary}',
      desc: {
        english: '',
        hindi: 'हृदय की मांसपेशी: धारीदार, शाखित, अंतर्संबंधित डिस्क, अनैच्छिक',
      },
    },
    {
      latex: '\\text{Earthworm: clitellum 14-16-17; female pore 14; male pore 18}',
      desc: {
        english: '',
        hindi: 'केंचुआ: क्लाइटेलम 14-16-17; महिला छिद्र 14; नर रंध्र 18',
      },
    },
    {
      latex: '\\text{Cockroach: 10 pairs spiracles; Malpighian tubules; uricotelic}',
      desc: {
        english: '',
        hindi: 'कॉकरोच: 10 जोड़े स्पाइरैकल; माल्पीघियन नलिकाएं; यूरिकोटेलिक',
      },
    },
    {
      latex: '\\text{Frog: heart 3-chambered (2A+1V); ureotelic; external fert.}',
      desc: {
        english: '',
        hindi: 'मेंढक: हृदय 3-कक्षीय (2A+1V); यूरियोटेलिक; बाहरी फर्ट.',
      },
    },
    {
      latex: '\\text{Cockroach abdomen: female 7 sterna, male 9 sterna + anal styles}',
      desc: {
        english: '',
        hindi: 'कॉकरोच का पेट: मादा 7 स्टर्ना, नर 9 स्टर्ना + गुदा शैलियाँ',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Tissue',
      problem: {
        english: 'Intercalated discs occur in:',
        hindi: 'इंटरकलेटेड डिस्क होती हैं:',
      },
      answer: {
        english: 'Cardiac muscle',
        hindi: 'हृदय की मांसपेशी',
      },
      solution: {
        english: 'Allow rapid communication between fibres.',
        hindi: 'तंतुओं के बीच तीव्र संचार की अनुमति दें।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Earthworm',
      problem: {
        english: 'Clitellum in earthworm is found in segments:',
        hindi: 'केंचुए में क्लाइटेलम खंडों में पाया जाता है:',
      },
      answer: {
        english: '14, 15, 16, 17',
        hindi: '14, 15, 16, 17',
      },
      solution: {
        english: 'Secretes cocoon during reproduction.',
        hindi: 'प्रजनन के दौरान कोकून स्रावित करता है।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Cockroach',
      problem: {
        english: 'Excretion in cockroach is mainly by:',
        hindi: 'कॉकरोच में उत्सर्जन मुख्य रूप से होता है:',
      },
      answer: {
        english: 'Malpighian tubules (uricotelic)',
        hindi: 'माल्पीघियन नलिकाएं (यूरिकोटेलिक)',
      },
      solution: {
        english: 'At midgut-hindgut junction.',
        hindi: 'मिडगुट-हिंदगुट जंक्शन पर।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Connective',
      problem: {
        english: 'Tendon connects:',
        hindi: 'टेंडन जुड़ता है:',
      },
      answer: {
        english: 'Muscle to bone',
        hindi: 'मांसपेशी से हड्डी तक',
      },
      solution: {
        english: 'Ligament: bone to bone.',
        hindi: 'लिगामेंट: हड्डी से हड्डी तक।',
      },
    },
    {
      year: 2018,
      exam: 'NEET',
      tag: 'Frog',
      problem: {
        english: 'Heart of frog is:',
        hindi: 'मेंढक का हृदय है:',
      },
      answer: {
        english: '3-chambered (2 atria + 1 ventricle)',
        hindi: '3-कक्षीय (2 अटरिया + 1 निलय)',
      },
      solution: {
        english: 'Mixed blood in ventricle.',
        hindi: 'निलय में मिश्रित रक्त.',
      },
    },
  ],
  next: {
    slug: 'transport-in-plants',
    title: 'Transport in Plants',
  },
};

export default chapter;
